import * as React from "react";
import {
    Grid, Paper, TextField, Typography, Button, IconButton, Box,
    Collapse, Divider, InputAdornment
} from "@mui/material";
import { Autocomplete } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CircularProgress from '@mui/material/CircularProgress';

import { money } from "../utils/format";
import { limitDecimals, normalizeOnBlur } from "../utils/number";
import { useDebouncedCallback } from "../hooks/useDebouncedCallback";
import type {
    ConceptMinimal, ProductoAgregado, ProductosProps
} from "../types/product";

const sxInputHeight = { "& .MuiInputBase-root": { height: 40 } };
const helperSx = { minHeight: 18, m: 0, mt: 0.5 };

type ItemProps = {
    p: ProductoAgregado;
    index: number;
    isOpen: boolean;
    setOpenIdx: (i: number | null) => void;
    onEliminar: (i: number) => void;
    agregando?: boolean;
};

const ConceptItem = React.memo(function ConceptItem({
    p, index, isOpen, setOpenIdx, onEliminar, agregando
}: ItemProps) {
    const precioUnitario = parseFloat(p.precio || "0");
    const subtotal = precioUnitario * (p.cantidad || 0);
    const totalTraslados =
        (p.impuestos?.traslados || [])?.reduce((acc, t) => acc + (Number(t?.importe) || 0), 0) || 0;
    const totalRetenciones =
        (p.impuestos?.retenciones || [])?.reduce((acc, r) => acc + (Number(r?.importe) || 0), 0) || 0;
    const impuestos = totalTraslados - totalRetenciones;
    const total = subtotal + impuestos;

    return (
        <Paper
            variant="outlined"
            sx={{
                mb: 1.25, p: 1.25, position: "relative", borderRadius: 2,
                boxShadow: "0 2px 6px rgba(0,0,0,.06)",
                borderLeft: "4px solid",
                borderLeftColor: index % 2 === 0 ? "primary.light" : "grey.400",
                backgroundColor: index % 2 === 0 ? "grey.50" : "background.paper",
            }}
        >
            <Box position="absolute" top={6} right={6} display="flex" gap={0.5}>
                <IconButton
                    size="small"
                    onClick={() => setOpenIdx(isOpen ? null : index)}
                    title={isOpen ? "Ocultar detalle" : "Ver detalle de impuestos"}
                >
                    <ExpandMoreIcon
                        fontSize="small"
                        sx={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform .2s" }}
                    />
                </IconButton>
                <IconButton
                    size="small"
                    color="error"
                    onClick={() => onEliminar(index)}
                    disabled={agregando}
                    title="Eliminar"
                >
                    <DeleteIcon fontSize="small" />
                </IconButton>
            </Box>

            <Typography
                variant="subtitle1"
                fontWeight={700}
                fontSize={14}
                textAlign="justify"
                sx={{ pr: 8, lineHeight: 1.25, mb: 0.5, letterSpacing: 0.2 }}
            >
                {p.descripcion || "—"}
            </Typography>

            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(5, 1fr)" },
                    gap: 1.25,
                    backgroundColor: "rgba(0,0,0,0.035)",
                    borderRadius: 1.5,
                    px: 2,
                    py: 1,
                }}
            >
                <Box>
                    <Typography variant="caption" color="text.secondary">Cantidad:</Typography>
                    <Typography fontSize={14} fontWeight={700}>{p.cantidad} {p.unidad || ""}</Typography>
                </Box>
                <Box>
                    <Typography variant="caption" color="text.secondary">Precio Unit.:</Typography>
                    <Typography fontSize={14} fontWeight={700}>{money(precioUnitario)}</Typography>
                </Box>
                <Box>
                    <Typography variant="caption" color="text.secondary">Impuestos:</Typography>
                    <Typography fontSize={14} fontWeight={700}>{money(impuestos)}</Typography>
                </Box>
                <Box>
                    <Typography variant="caption" color="text.secondary">Subtotal:</Typography>
                    <Typography fontSize={14} fontWeight={700} sx={{ color: "primary.main" }}>{money(subtotal)}</Typography>
                </Box>
                <Box>
                    <Typography variant="caption" color="text.secondary">Total:</Typography>
                    <Typography fontSize={14} fontWeight={800} sx={{ color: "success.main" }}>{money(total)}</Typography>
                </Box>
            </Box>

            <Collapse in={isOpen} unmountOnExit>
                <Divider sx={{ my: 1.25 }} />
                <Box sx={{ px: 1 }}>
                    {(p.impuestos?.traslados?.length || 0) > 0 && (
                        <Box sx={{ mb: 0.75 }}>
                            <Typography variant="caption" color="text.secondary">Traslados</Typography>
                            {p.impuestos!.traslados!.map((t, i) => (
                                <Box key={`t-${i}`} sx={{ display: "flex", justifyContent: "space-between", fontSize: 13, py: 0.25 }}>
                                    <span>
                                        {t.impuesto} · {t.tipofactor}
                                        {t.tasaocuota != null ? ` · Tasa ${t.tasaocuota}` : ""}
                                    </span>
                                    <span>{money(Number(t.importe || 0))}</span>
                                </Box>
                            ))}
                        </Box>
                    )}

                    {(p.impuestos?.retenciones?.length || 0) > 0 && (
                        <Box sx={{ mb: 0.75 }}>
                            <Typography variant="caption" color="text.secondary">Retenciones</Typography>
                            {p.impuestos!.retenciones!.map((r, i) => (
                                <Box key={`r-${i}`} sx={{ display: "flex", justifyContent: "space-between", fontSize: 13, py: 0.25 }}>
                                    <span>
                                        {r.impuesto} · {r.tipofactor}
                                        {r.tasaocuota != null ? ` · Tasa ${r.tasaocuota}` : ""}
                                    </span>
                                    <span>-{money(Number(r.importe || 0))}</span>
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>
            </Collapse>
        </Paper>
    );
}, (prev, next) =>
    prev.p === next.p &&
    prev.index === next.index &&
    prev.isOpen === next.isOpen &&
    prev.agregando === next.agregando
);

export default function Products(props: ProductosProps) {
    const {
        conceptos, selectedConcept, setSelectedConcept,
        cantidad, setCantidad, precio, setPrecio,
        unidad, setUnidad, descripcion, setDescripcion,
        onAgregar, productosAgregados, onEliminar, onLimpiar,
        agregando = false, impuestoPreview = "",
    } = props;

    const [openIdx, setOpenIdx] = React.useState<number | null>(null);

    // descripción con debounce
    const [descLocal, setDescLocal] = React.useState(descripcion);
    React.useEffect(() => { setDescLocal(descripcion); }, [descripcion]);
    const commitDescripcion = useDebouncedCallback((v: string) => setDescripcion(v), 150);

    // estados locales string para cantidad y precio
    const [cantidadLocal, setCantidadLocal] = React.useState<string>("");
    const [precioLocal, setPrecioLocal] = React.useState<string>("");

    React.useEffect(() => {
        setCantidadLocal(cantidad != null ? String(cantidad) : "");
    }, [cantidad]);

    React.useEffect(() => {
        setPrecioLocal(precio ?? "");
    }, [precio, selectedConcept]);

    const hasConcept = !!selectedConcept;

    // --- VALIDACIONES ---
    const qtyNumber = Number((cantidadLocal || "").replace(/,/g, "."));
    const qtyEmpty = hasConcept && (!cantidadLocal || cantidadLocal.trim() === "");
    const qtyNonPositive = hasConcept && !qtyEmpty && (!Number.isFinite(qtyNumber) || qtyNumber <= 0);
    const qtyError = qtyEmpty || qtyNonPositive;

    const priceNumber = Number((precioLocal || "").replace(/,/g, "."));
    const priceEmpty = hasConcept && (!precioLocal || precioLocal.trim() === "");
    const priceNonPositive = hasConcept && !priceEmpty && (!Number.isFinite(priceNumber) || priceNumber <= 0);
    const priceError = priceEmpty || priceNonPositive;

    const descEmpty = hasConcept && (!descLocal || descLocal.trim() === "");
    const canAdd = hasConcept && !qtyError && !priceError && !descEmpty;

    const handleAgregarClick = React.useCallback(() => {
        if (agregando || !canAdd) return;
        onAgregar();
    }, [agregando, canAdd, onAgregar]);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key !== "Enter") return;
        const target = e.target as HTMLElement | null;
        if (target?.tagName?.toLowerCase() === "textarea") return;
        if (canAdd) {
            e.preventDefault();
            handleAgregarClick();
        }
    };

    return (
        <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 3, boxShadow: "0 2px 6px rgba(0,0,0,.06)" }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="subtitle1" fontWeight={600}>Productos y Servicios</Typography>
                <Box display="flex" alignItems="center" gap={2}>
                    <Typography variant="body2" color="text.secondary">
                        {productosAgregados.length} concepto{productosAgregados.length === 1 ? "" : "s"}
                    </Typography>
                    {onLimpiar && productosAgregados.length > 0 && (
                        <Button size="small" color="inherit" onClick={onLimpiar} disabled={agregando}>
                            Limpiar lista
                        </Button>
                    )}
                </Box>
            </Box>

            <Grid container spacing={1} alignItems="flex-start" onKeyDown={handleKeyDown}>
                <Grid item xs={12}>
                    <Autocomplete<ConceptMinimal, false, false, false>
                        options={conceptos}
                        value={selectedConcept}
                        getOptionLabel={(option) => option?.concept || ""}
                        isOptionEqualToValue={(o, v) => o?.concept === v?.concept}
                        onChange={(_, value) => {
                            setSelectedConcept(value);
                            setDescLocal(value?.descripcion || "");
                            commitDescripcion(value?.descripcion || "");
                            setUnidad(value?.unidad || "");
                            const p = value?.valor_unitario != null ? String(value.valor_unitario) : "";
                            setPrecio(p);
                            setPrecioLocal(p);
                        }}
                        disablePortal fullWidth size="small" disabled={agregando}
                        renderInput={(params) => (
                            <TextField {...params} label="Buscar concepto / descripción" size="small" sx={sxInputHeight} autoFocus />
                        )}
                    />
                </Grid>

                {/* Cantidad */}
                <Grid item xs={12} md={2} sx={{ mt: 1 }}>
                    <TextField
                        label="Cantidad"
                        value={cantidadLocal}
                        onChange={(e) => {
                            const v = e.target.value;
                            if (v === "" || v === "." || v === "-") { setCantidadLocal(v); return; }
                            setCantidadLocal(limitDecimals(v, 2));
                        }}
                        onBlur={() => {
                            const norm = normalizeOnBlur(cantidadLocal, 2);
                            setCantidadLocal(norm);
                            props.setCantidad(norm ? Number(norm) : 0);
                        }}
                        size="small"
                        fullWidth
                        sx={sxInputHeight}
                        inputMode="decimal"
                        disabled={agregando || !hasConcept}
                        error={qtyError}
                        helperText={
                            !hasConcept ? " " :
                                qtyEmpty ? "La cantidad es obligatoria" :
                                    qtyNonPositive ? "La cantidad debe ser mayor a 0" : " "
                        }
                        FormHelperTextProps={{ sx: helperSx }}
                    />
                </Grid>

                {/* Precio */}
                <Grid item xs={12} md={2} sx={{ mt: 1 }}>
                    <TextField
                        label="Precio *"
                        value={precioLocal}
                        onChange={(e) => {
                            const v = e.target.value;
                            if (v === "" || v === "." || v === "-") { setPrecioLocal(v); return; }
                            setPrecioLocal(limitDecimals(v, 6));
                        }}
                        onBlur={() => {
                            const norm = normalizeOnBlur(precioLocal, 6);
                            setPrecioLocal(norm);
                            props.setPrecio(norm);
                        }}
                        size="small"
                        fullWidth
                        sx={sxInputHeight}
                        disabled={agregando || !hasConcept}
                        error={priceError}
                        helperText={
                            !hasConcept ? " " :
                                priceEmpty ? "El precio es obligatorio" :
                                    priceNonPositive ? "El precio debe ser mayor a 0" : " "
                        }
                        FormHelperTextProps={{ sx: helperSx }}
                        InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                        inputMode="decimal"
                    />
                </Grid>

                {/* Unidad */}
                <Grid item xs={12} md={2} sx={{ mt: 1 }}>
                    <TextField
                        label="Unidad"
                        value={unidad}
                        size="small"
                        fullWidth
                        sx={sxInputHeight}
                        disabled={agregando || !hasConcept}
                        helperText={" "}
                        FormHelperTextProps={{ sx: helperSx }}
                    />
                </Grid>

                {/* Impuesto calculado */}
                <Grid item xs={12} md={3} sx={{ mt: 1 }}>
                    <TextField
                        label="Impuesto calculado"
                        value={impuestoPreview}
                        size="small"
                        fullWidth
                        sx={sxInputHeight}
                        InputProps={{ readOnly: true }}
                        placeholder="—"
                        helperText={" "}
                        FormHelperTextProps={{ sx: helperSx }}
                    />
                </Grid>

                <Grid item xs={12} md={3} sx={{ mt: 1 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        onClick={handleAgregarClick}
                        sx={{ height: 40 }}
                        disabled={!canAdd || agregando}
                        aria-busy={agregando ? "true" : "false"}
                    >
                        {agregando ? <CircularProgress  size="30px"/> : "Agregar"}
                    </Button>
                </Grid>

                {/* Descripción */}
                <Grid item xs={12} md={12}>
                    <TextField
                        multiline
                        rows={3}
                        placeholder="Descripción detallada del producto o servicio..."
                        fullWidth
                        value={descLocal}
                        onChange={(e) => { const v = e.target.value; setDescLocal(v); commitDescripcion(v); }}
                        disabled={agregando || !hasConcept}
                        error={descEmpty}
                        helperText={hasConcept && descEmpty ? "La descripción es obligatoria" : " "}
                        FormHelperTextProps={{ sx: helperSx }}
                    />
                </Grid>
            </Grid>

            <Box mt={2} sx={{ maxHeight: { xs: 300, md: 420 }, overflowY: "auto", pr: 1 }}>
                {productosAgregados.length === 0 && (
                    <Box py={4} textAlign="center" color="text.secondary">
                        <Typography variant="body2">Aún no has agregado conceptos.</Typography>
                        <Typography variant="body2">Busca un concepto, completa cantidad y precio, y presiona “Agregar”.</Typography>
                    </Box>
                )}

                {productosAgregados.map((p, i) => (
                    <ConceptItem
                        key={i}
                        p={p} index={i}
                        isOpen={i === openIdx}
                        setOpenIdx={setOpenIdx}
                        onEliminar={props.onEliminar}
                        agregando={agregando}
                    />
                ))}
            </Box>
        </Paper>
    );
}
